"use client"

import { useCallback, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { Flag, Loader2, MoreVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ReportDialog } from "@/components/ui/report-dialog"
import { chatStickyIconButtonClass } from "@/components/chat/chat-sticky-header"
import { resolveChatReportTarget, type ChatReportTarget } from "@/lib/chat-report"
import { useToast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"

type ChatConversationReportMenuProps = {
  roomName: string
  currentUserId: string
  otherProfile?: { username?: string; avatar_url?: string | null } | null
  otherId?: string
}

export function ChatConversationReportMenu({
  roomName,
  currentUserId,
  otherProfile,
  otherId: otherIdProp,
}: ChatConversationReportMenuProps) {
  const tReport = useTranslations("report")
  const tChat = useTranslations("chat")
  const { toast } = useToast()
  const [menuOpen, setMenuOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [reportTarget, setReportTarget] = useState<ChatReportTarget | null>(null)

  const otherId = useMemo(() => {
    if (otherIdProp) return otherIdProp
    return roomName.split(":").find((part) => part !== currentUserId)
  }, [currentUserId, otherIdProp, roomName])

  const handleReport = useCallback(async () => {
    if (!roomName || loading) return
    setLoading(true)
    setMenuOpen(false)
    try {
      const result = await resolveChatReportTarget(
        roomName,
        currentUserId,
        otherProfile ?? undefined,
        otherId,
      )
      if ("kind" in result) {
        if (result.kind === "no_messages") {
          toast({
            title: "No messages to report",
            description: "This chat has no messages yet.",
          })
          return
        }
        if (result.kind === "no_other_messages") {
          toast({
            title: "No messages to report",
            description: "No messages from the other user yet.",
          })
          return
        }
        toast({
          title: tChat("failedReport"),
          description: result.message || tChat("tryAgain"),
        })
        return
      }
      setReportTarget(result)
      setDialogOpen(true)
    } catch (err: unknown) {
      toast({
        title: tChat("failedReport"),
        description: err instanceof Error ? err.message : tChat("tryAgain"),
      })
    } finally {
      setLoading(false)
    }
  }, [currentUserId, loading, otherId, otherProfile, roomName, tChat, toast])

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={tReport("moreActions")}
            className={cn(chatStickyIconButtonClass, loading && "pointer-events-none opacity-60")}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-foreground" />
            ) : (
              <MoreVertical className="h-5 w-5 text-foreground" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" surface="glass">
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault()
              void handleReport()
            }}
            className="text-red-500 focus:text-red-500"
          >
            <Flag className="mr-2 h-4 w-4" />
            {tChat("reportChat")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {reportTarget ? (
        <ReportDialog
          open={dialogOpen}
          onOpenChangeAction={(next) => {
            setDialogOpen(next)
            if (!next) setReportTarget(null)
          }}
          itemType={reportTarget.itemType}
          itemId={reportTarget.itemId}
          itemTitle={reportTarget.itemTitle}
          currentUserId={currentUserId}
          reportedUserId={reportTarget.reportedUserId}
          reportedUsername={reportTarget.reportedUsername}
          reportedAvatarUrl={reportTarget.reportedAvatarUrl}
        />
      ) : null}
    </>
  )
}
